import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { useLocation } from 'react-router-dom'
import { CircularProgress } from '@material-ui/core'
import { Redirect } from 'src/components/Redirect'
import { Divider } from 'src/components/Divider'
import ROUTES from 'src/constants/routes'
import { useStyles } from './styles'
import { CloudDownload, KeyboardArrowDown, KeyboardArrowUp, Close, ExitToApp } from '@material-ui/icons'
import { useSideMenu } from './_hooks/useSideMenu'
import { useDevelopmentBanner } from 'src/hooks/useDevelopmentBanner'
import { FarmSelector } from './components/FarmSelector'

let sideMenuScrollPosition = 0

interface SideMenuProps {
  close: React.MouseEventHandler
  isOpen?: boolean
}

const SideMenu: React.FC<SideMenuProps> = ({ close }) => {
  const { pathname } = useLocation()
  const styles = useStyles()

  const { profile, userLogged, menus, handleLogout } = useSideMenu()
  const { banner } = useDevelopmentBanner()
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const isRestoringRef = useRef(false)
  const prevPathnameRef = useRef(pathname)

  if (prevPathnameRef.current !== pathname) {
    prevPathnameRef.current = pathname
    isRestoringRef.current = true
  }

  const toggleDropdown = (name: string) => {
    setOpenDropdown((prev) => (prev === name ? null : name))
  }

  const handleContainerScroll = useCallback(() => {
    if (!isRestoringRef.current && scrollContainerRef.current) {
      sideMenuScrollPosition = scrollContainerRef.current.scrollTop
    }
  }, [])

  useLayoutEffect(() => {
    if (scrollContainerRef.current && sideMenuScrollPosition > 0) {
      scrollContainerRef.current.scrollTop = sideMenuScrollPosition
    }
  }, [])

  useEffect(() => {
    const parent = menus.find((menu) =>
      menu.children?.some((child) => {
        const hasParams = child.path.includes('/:')
        const baseChildPath = hasParams ? child.path.replace(/\/:[^/]+.*$/, '') : child.path
        return hasParams
          ? pathname.startsWith(baseChildPath + '/')
          : pathname === child.path || pathname.startsWith(child.path + '/')
      })
    )

    if (parent) {
      setOpenDropdown(parent.name)
    } else {
      setOpenDropdown(null)
    }

    isRestoringRef.current = true
    const savedPosition = sideMenuScrollPosition

    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = savedPosition
    }

    const timer = setTimeout(() => {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = savedPosition
      }
      isRestoringRef.current = false
    }, 350)

    return () => {
      clearTimeout(timer)
      isRestoringRef.current = false
    }
  }, [pathname, menus])

  return (
    <div className={styles.sideMenuWrapper}>
      <div ref={scrollContainerRef} onScroll={handleContainerScroll} className={styles.sideMenuContainer}>
        <button className={styles.closeButton} onClick={close}>
          <Close />
        </button>
        <h1 className='side-menu__title'>CONGADO</h1>
        <p className='side-menu__sub-title'>Sistema de Gestão de Rebanho</p>

        <FarmSelector />

        <nav>
          <ul className={styles.sideMenuList}>
            {/* {menus.map(({ name, path, icon: Icon, stickyValue, children }) => { */}
            {menus.map(({ name, path, icon: Icon, children }) => {
              const hasActiveChild = children
                ? children.some((child) => {
                    const hasParams = child.path.includes('/:')
                    const baseChildPath = hasParams ? child.path.replace(/\/:[^/]+.*$/, '') : child.path
                    return hasParams
                      ? pathname.startsWith(baseChildPath + '/')
                      : pathname === child.path || pathname.startsWith(child.path + '/')
                  })
                : false

              const isActive = children ? false : pathname.startsWith(path)

              const hasOpenSubmenu = openDropdown === name

              return (
                <React.Fragment key={name}>
                  <li
                    className={`side-menu__list-item${isActive ? ' side-menu__list-item--selected' : ''}${
                      hasOpenSubmenu ? ' side-menu__list-item--expanded' : ''
                    }${hasActiveChild ? ' side-menu__list-item--has-active-child' : ''}`}
                  >
                    {children ? (
                      <button
                        type='button'
                        className={`${styles.sideMenuItem} ${styles.sideMenuItemDropdown}`}
                        onTouchEnd={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          toggleDropdown(name)
                        }}
                        onClick={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          toggleDropdown(name)
                        }}
                      >
                        <div className={styles.sideMenuItemContent}>
                          {Icon && <Icon />}
                          <span className='side-menu__description'>{name}</span>
                        </div>
                        <span className={styles.sideMenuToggle}>
                          {openDropdown === name ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </span>
                      </button>
                    ) : (
                      <div onClick={close}>
                        <Redirect to={path} className={styles.sideMenuItem}>
                          {Icon && <Icon />}
                          <div className='side-menu__description-container'>
                            <span className='side-menu__description'>{name}</span>
                            {/* {stickyValue ?
                              <span className='side-menu__sticky-value'>{stickyValue}</span> : null} */}
                          </div>
                        </Redirect>
                      </div>
                    )}
                  </li>

                  {children && (
                    <ul className={`${styles.sideMenuSubList}${openDropdown === name ? ' open' : ''}`}>
                      {children.map(({ name: subName, path: subPath, icon: SubIcon }) => {
                        const hasParams = subPath.includes('/:')
                        const baseSubPath = hasParams ? subPath.replace(/\/:[^/]+.*$/, '') : subPath
                        const isSubItemActive = hasParams
                          ? pathname.startsWith(baseSubPath + '/')
                          : pathname === subPath || pathname.startsWith(subPath + '/')

                        return (
                          <li
                            key={subName}
                            className={`side-menu__list-item${
                              isSubItemActive ? ' side-menu__list-item--selected' : ''
                            }`}
                          >
                            <div onClick={close}>
                              <Redirect to={subPath} className={styles.sideMenuItem}>
                                {SubIcon && <SubIcon />}
                                <span className='side-menu__description'>{subName}</span>
                              </Redirect>
                            </div>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </React.Fragment>
              )
            })}

            {/* Download App */}
            <div className={styles.SideMenuDownloadApp}>
              <CloudDownload />
              <a
                href='https://play.google.com/store/apps/details?id=br.com.congado.app'
                target='_blank'
                rel='noreferrer'
                className='side-menu__download-app'
              >
                Download App
              </a>
            </div>

            <Divider />

            {/* Logout */}
            <li className='side-menu__list-item'>
              <button
                type='button'
                className={styles.sideMenuItem}
                disabled={isLoggingOut}
                onClick={async () => {
                  setIsLoggingOut(true)
                  await new Promise((r) => setTimeout(r, 800))
                  await handleLogout()
                }}
              >
                <ExitToApp />
                <span className='side-menu__description'>Sair</span>
              </button>
            </li>

            {/* Usuário */}
            {userLogged.map(({ user }) => (
              <li
                key={user}
                className={`side-menu__li_user${pathname === ROUTES.PROFILE ? ` ${styles.sideMenuUserSelected}` : ''}`}
                onClick={close}
              >
                <div className={styles.sideMenuUser}>
                  <span className={styles.sideMenuUserLetra}>{user?.[0] || ''}</span>
                  <Redirect to={ROUTES.PROFILE} className='side-menu__userName'>
                    <span>{user}</span>
                    <span className='side-menu__profile'>{profile}</span>
                  </Redirect>
                </div>
              </li>
            ))}
          </ul>
        </nav>

        {banner}
      </div>

      {isLoggingOut &&
        ReactDOM.createPortal(
          <div className={styles.logoutOverlay}>
            <div className={styles.logoutContent}>
              <CircularProgress size={48} style={{ color: '#fff' }} />
              <span className={styles.logoutText}>Saindo...</span>
            </div>
          </div>,
          document.body
        )}
    </div>
  )
}

export default SideMenu
